import React from "react";
import Link from "next/link";
import { twMerge } from "tailwind-merge";

type ButtonVariant =
  | "primary"
  | "secondary"
  | "tertiary"
  | "outline"
  | "ghost"
  | "destructive";
type ButtonSize = "xs" | "sm" | "md" | "lg";

export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  fullWidth?: boolean;
  loading?: boolean;
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
  rounded?: "md" | "lg" | "xl" | "full";
  /** When provided the button renders as a Next.js <Link> instead of <button>. */
  href?: string;
}

const baseStyles =
  "inline-flex items-center justify-center gap-2 whitespace-nowrap font-semibold transition-all duration-200 ease-out focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 active:scale-[0.98]";

const variants: Record<ButtonVariant, string> = {
  /**
   * Green CTA button from Figma
   */
  primary:
    "bg-[var(--color-primary)] text-white! hover:brightness-95  transition-all hover:ring-4 hover:ring-[var(--color-ring)]",

  /**
   * White bordered button from Figma
   */
  secondary:
    "bg-white text-black border border-[#E8E6E3] hover:bg-[#F8F8F8] focus:ring-[#D9D9D9]",

  /**
   * Bright lime green CTA button from Figma (tertiary color)
   */
  tertiary:
    "bg-[var(--color-tertiary)] text-black! hover:brightness-95 focus:ring-[var(--color-tertiary)]",

  /**
   * Transparent bordered variant
   */
  outline:
    "border border-[#D9D9D9] bg-transparent text-black hover:bg-[#F5F5F5] focus:ring-[#D9D9D9]",

  destructive: "bg-red-500 text-white hover:bg-red-600 focus:ring-red-500",

  /**
   * Minimal text button
   */
  ghost: "bg-transparent text-black hover:bg-black/5 focus:ring-gray-300",
};

const sizes: Record<ButtonSize, string> = {
  xs: "h-8 px-3 text-xs rounded-xl",
  sm: "h-10 px-4 text-sm rounded-2xl",
  md: "h-14 px-8 text-lg rounded-3xl",
  lg: "h-16 px-10 text-xl rounded-3xl",
};

const roundedStyles = {
  md: "rounded-xl",
  lg: "rounded-2xl",
  xl: "rounded-3xl",
  full: "rounded-full",
};

function buildClassName(
  variant: ButtonVariant,
  size: ButtonSize,
  rounded: ButtonProps["rounded"],
  fullWidth: boolean,
  extra: string,
): string {
  return twMerge(
    [
      baseStyles,
      variants[variant],
      sizes[size],
      rounded ? roundedStyles[rounded] : "",
      fullWidth ? "w-full" : "",
      extra,
    ]
      .filter(Boolean)
      .join(" "),
  );
}

export function Button({
  children,
  variant = "primary",
  size = "md",
  fullWidth = false,
  loading = false,
  leftIcon,
  rightIcon,
  rounded,
  className = "",
  disabled,
  type = "button",
  href,
  ...props
}: ButtonProps) {
  const classes = buildClassName(variant, size, rounded, fullWidth, className);

  const inner = loading ? (
    <>
      <svg
        className="h-4 w-4 animate-spin"
        xmlns="http://www.w3.org/2000/svg"
        fill="none"
        viewBox="0 0 24 24"
        aria-hidden="true"
      >
        <circle
          cx="12"
          cy="12"
          r="10"
          stroke="currentColor"
          strokeWidth="2"
          className="opacity-20"
        />
        <path
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          d="M22 12c0-5.523-4.477-10-10-10"
        />
      </svg>
      <span>Loading...</span>
    </>
  ) : (
    <>
      {leftIcon && (
        <span className="flex shrink-0 items-center">{leftIcon}</span>
      )}
      <span>{children}</span>
      {rightIcon && (
        <span className="flex shrink-0 items-center">{rightIcon}</span>
      )}
    </>
  );

  if (href) {
    return (
      <Link href={href} className={classes}>
        {inner}
      </Link>
    );
  }

  return (
    <button
      type={type}
      disabled={disabled || loading}
      className={
        classes +
        " cursor-pointer transition-all disabled:cursor-not-allowed! disabled:bg-gray-400"
      }
      {...props}
    >
      {inner}
    </button>
  );
}

export default Button;
